import { useEffect, useState } from "react";
import { Field } from "redux-form";
import { toastr } from "react-redux-toastr";
import Select from "react-select/async";
import debounce from "debounce-promise";

import { client, getApiErrorMessage } from "utils/api";
import getCnCodes from "utils/select_options/getCnCodes";

const select_styles = {
  control: (styles, { selectProps }) => ({
    ...styles,
    borderColor: selectProps.isError ? "#fd397a" : "#E2E5EC",
    minHeight: 30,
  }),
  option: (provided) => ({
    ...provided,
    fontFamily: "Poppins",
    fontSize: "13px",
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "0 10px",
  }),
  singleValue: (provided) => ({
    ...provided,
    fontFamily: "Poppins",
    fontSize: "13px",
    padding: 0,
    maxWidth: "calc(100% - 8px)",
  }),
  indicatorSeparator: (styles) => ({
    ...styles,
    display: "none",
  }),
  placeholder: (provided) => ({
    ...provided,
    fontFamily: "Poppins",
    fontSize: "13px",
  }),
};

// W menu pokazujemy kod + pelna sciezke slownika, po wybraniu kod + nazwe.
const formatOptionLabel = (option, { context }) => {
  if (context === "menu") {
    return (
      <div>
        <strong>{option.value}</strong>
        {option.full_path_pl && (
          <div style={{ fontSize: 11, color: "#74788d", whiteSpace: "normal" }}>
            {option.full_path_pl}
          </div>
        )}
      </div>
    );
  }
  return (
    <span>
      <strong>{option.value}</strong>
      {option.description_pl && ` — ${option.description_pl}`}
    </span>
  );
};

const loadCnCodes = debounce((q) => getCnCodes(q), 500, {
  leading: false,
  accumulate: false,
});

// Jedno pole edycji kodu CN: autocomplete po slowniku (ISZTAR), wybrana
// wartosc pokazuje kod + nazwe, a pod polem pelna sciezke klasyfikacji.
const CnCodeSelectField = ({ input, meta, onManualSelect }) => {
  const [selected, setSelected] = useState(null);
  const isError = meta.touched && meta.invalid;

  // Dociagniecie nazwy/sciezki dla kodu ustawionego z zewnatrz
  // (initialValues, prefill z taksonu, klasyfikacja AI).
  useEffect(() => {
    if (!input.value) {
      setSelected(null);
      return;
    }
    if (selected?.value === input.value) return;

    let active = true;
    getCnCodes(input.value).then((options) => {
      if (!active) return;
      const match = options.find(({ value }) => value === input.value);
      setSelected(
        match || {
          value: input.value,
          label: input.value,
          description_pl: "(brak w słowniku CN)",
          full_path_pl: null,
        }
      );
    });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input.value]);

  return (
    <div className={`form-group ${isError ? "error" : ""}`}>
      <div className="inp-wrapper">
        <label>Kod CN (8 cyfr)</label>
        <Select
          value={selected}
          isError={isError}
          isClearable
          styles={select_styles}
          placeholder="Wpisz kod lub nazwę towaru..."
          noOptionsMessage={({ inputValue }) =>
            inputValue ? "Brak wyników w słowniku CN" : "Wpisz kod lub nazwę"
          }
          loadingMessage={() => "Szukam..."}
          formatOptionLabel={formatOptionLabel}
          loadOptions={loadCnCodes}
          onChange={(option) => {
            setSelected(option);
            input.onChange(option?.value || null);
            onManualSelect();
          }}
          onBlur={() => input.onBlur()}
          theme={(theme) => ({
            ...theme,
            borderRadius: 4,
            colors: { ...theme.colors, neutral20: "#E2E5EC" },
          })}
        />
        {selected?.full_path_pl && (
          <p style={{ fontSize: 11, color: "#74788d", marginTop: 4 }}>
            {selected.full_path_pl}
          </p>
        )}
        {isError && meta.error && <p className="inp-error">{meta.error}</p>}
      </div>
    </div>
  );
};

const confidenceColor = (value) => {
  if (value >= 0.85) return "#0abb87";
  if (value >= 0.6) return "#ffb822";
  return "#fd397a";
};

const ConfidenceBadge = ({ value }) => (
  <span
    style={{
      display: "inline-block",
      padding: "2px 8px",
      borderRadius: 10,
      fontSize: 11,
      fontWeight: 600,
      color: "#fff",
      backgroundColor: confidenceColor(value),
      whiteSpace: "nowrap",
    }}
  >
    Pewność AI: {Math.round(value * 100)}%
  </span>
);

const source_labels = {
  auto_batch: "klasyfikacja automatyczna (batch)",
  form_suggestion: "sugestia w formularzu",
  taxon_default: "domyślny kod kategorii",
};

// Sekcja kodu CN: jedno pole (autocomplete slownika ISZTAR) + klasyfikacja AI.
// Pracownik zapisujac formularz potwierdza propozycje albo zmienia kod
// (zmiana reczna czysci flage cn_code_from_ai -> backend oznaczy 'manual').
const CnCodeSection = ({
  change,
  name,
  root_taxon_id,
  product_group_id,
  ai_meta,
}) => {
  const [is_classifying, setClassifying] = useState(false);
  const [suggestion, setSuggestion] = useState(null);

  const can_classify = Boolean(product_group_id || name);

  const classify = async () => {
    try {
      setClassifying(true);
      const { data } = await client.post(
        "/cn_codes/classify",
        product_group_id ? { product_group_id } : { name, root_taxon_id }
      );
      setSuggestion(data);
      change("cn_code", data.cn_code);
      change("cn_code_from_ai", true);
    } catch (error) {
      toastr.error(
        "Błąd",
        getApiErrorMessage(error, "Klasyfikacja AI nie powiodła się")
      );
    } finally {
      setClassifying(false);
    }
  };

  // Swieza sugestia z formularza ma pierwszenstwo przed zapisanymi
  // metadanymi z klasyfikacji wsadowej.
  const ai_info = suggestion
    ? {
        confidence: suggestion.confidence,
        justification: suggestion.justification,
        source: "form_suggestion",
        alternatives: suggestion.alternatives || [],
      }
    : ai_meta?.ai_cn_justification
    ? {
        confidence: ai_meta.ai_cn_confidence,
        justification: ai_meta.ai_cn_justification,
        source: ai_meta.ai_cn_source,
        alternatives: (ai_meta.ai_cn_alternatives?.alternatives || []).map(
          (code) => ({ cn_code: code })
        ),
      }
    : null;

  return (
    <>
      <div className="row m-b-10">
        <div className="box-12">
          <Field
            name="cn_code"
            component={CnCodeSelectField}
            onManualSelect={() => change("cn_code_from_ai", false)}
          />
        </div>
      </div>
      <div className="row m-b-10">
        <div className="box-12">
          <button
            type="button"
            className="btn btn-sm btn-blue"
            disabled={!can_classify || is_classifying}
            onClick={classify}
          >
            <span>
              {is_classifying ? "Klasyfikuję..." : "Zaproponuj kod CN (AI)"}
            </span>
          </button>
        </div>
      </div>
      {ai_info && (
        <div className="row m-b-10">
          <div className="box-12">
            <div
              style={{
                border: "1px solid #E2E5EC",
                borderLeft: `3px solid ${
                  ai_info.confidence != null
                    ? confidenceColor(ai_info.confidence)
                    : "#5d78ff"
                }`,
                borderRadius: 4,
                padding: "10px 12px",
                backgroundColor: "#f7f8fa",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 6,
                  gap: 8,
                }}
              >
                <strong style={{ fontSize: 12 }}>
                  Dlaczego AI wybrało ten kod?
                </strong>
                {ai_info.confidence != null && (
                  <ConfidenceBadge value={ai_info.confidence} />
                )}
              </div>
              <p style={{ fontSize: 12, lineHeight: 1.5 }}>
                {ai_info.justification}
              </p>
              {ai_info.alternatives?.length > 0 && (
                <p style={{ fontSize: 12, marginTop: 6 }}>
                  Alternatywy:{" "}
                  {ai_info.alternatives.map(({ cn_code, cn_description }) => (
                    <button
                      key={cn_code}
                      type="button"
                      className="btn-link"
                      title={cn_description}
                      style={{ marginRight: 8 }}
                      onClick={() => {
                        change("cn_code", cn_code);
                        change("cn_code_from_ai", false);
                      }}
                    >
                      {cn_code}
                    </button>
                  ))}
                </p>
              )}
              {ai_info.source && source_labels[ai_info.source] && (
                <p style={{ fontSize: 11, color: "#74788d", marginTop: 6 }}>
                  Źródło: {source_labels[ai_info.source]}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CnCodeSection;
